import { Box, Container, Typography, createStyles, makeStyles } from '@material-ui/core';
import cn from 'classnames';
import * as React from 'react';
import { Redirect } from 'react-router';
import DebtSeparatorWithFlower from '../../components/GeneralComponents/DebtSeparatorWithFlower';
import WaCard from '../../components/GeneralComponents/WaCard';
import IconDialog from '../../components/IconDialog/IconDialog';
import DebtCard from '../../components/NegotiationDebts/DebtCard';
import DebtIntroductionText from '../../components/PortalCajaSocial/DebtIntroductionText';
import useDictionary from '../../dictionaries/context/useDictionary';
import RenderText from '../../dictionaries/RenderText';
import { DebtType, OutDebt, OutDebtExtra, useGetOutDebtsQuery } from '../../generated/graphql';
import { session } from '../../utils/auth';
import { getNegBooleans } from '../../utils/getNegBooleans';
import { loadingMessage } from '../../utils/loadingMessage';
import { useGetProfile } from '../../utils/useGetProfile';
import { TranquiTheme } from '../App/theme';
import TitleAndSubTitleText from './TitleAndSubtitleText';
import { transformInfoBank } from './transformInfoBank';

interface SelectDebtProps { }

const SelectDebt: React.FC<SelectDebtProps> = () => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { gT } = useDictionary();

  const { data: outDebtsData, loading: outDebtsLoading } = useGetOutDebtsQuery({ fetchPolicy: 'network-only' });
  const { data: profileData, loading: profileLoading } = useGetProfile();

  const outDebts = React.useMemo(() => outDebtsData?.outDebts || [], [ outDebtsData ]);

  const debtId = React.useMemo(() => {
    if (outDebts.length === 1) {
      const [ debt ] = outDebts;
      return debt.id;
    }

    return undefined;
  }, [ outDebts ]);

  if (outDebtsLoading || profileLoading) {
    return (
      <IconDialog icon="loading" rotating open>
        <Typography align="center" variant="h3">{loadingMessage}</Typography>
      </IconDialog>
    );
  }

  const { profile } = profileData || {};
  const virtualDebts = outDebts.filter(({ type }) => type === DebtType.Virtual);

  if (!session.identityValidated) {
    // if hasn't confirmed identity redirect to validation
    return <Redirect to="/portal/validacion-identidad" />;
  }

  if (outDebts.length === 0) return <Redirect to="/portal" />;
  if (debtId) return <Redirect to={`/portal/deuda/${debtId}`} />;

  const firstDebt = outDebts[0] as OutDebt;
  const { isTranqui, isBogota } = getNegBooleans(firstDebt);
  const subtitle = isTranqui && isBogota ? 'Opciones de acuerdos de recuperación de cartera' : 'Opciones de negociación';
  return (
    <Container className={classes.root}>
      <TitleAndSubTitleText
        className="mb-4"
        title={`${gT('general_debtsView_title_defaultView_CONFIRMED_BY_USER')}`}
        subtitle={subtitle}
      />

      <DebtIntroductionText
        name={profile?.firstname || ''}
        bank={transformInfoBank(firstDebt)?.bank}
        document={profile?.document || ''}
        variant="select"
        extra={firstDebt.extra as OutDebtExtra}
      />
      <DebtSeparatorWithFlower />
      {
        !!virtualDebts.length
          ? (
            <>
              <Typography
                className={cn(classes.debtsDescription, 'mb-3')}
                data-test="cy-generalIntroductionVirtual-text"
              >
                <RenderText
                  textKey="general_debtsView_welcome_virtual_description"
                  isMarkdown
                />
              </Typography>
              <Box className={classes.virtualDebtsBox} data-test="cy-virtualDebts-box">
                {virtualDebts.map((debt) => (<DebtCard key={debt.id} debt={debt as OutDebt} />))}
              </Box>
              <Typography
                className={cn(classes.debtsDescription, 'my-3')}
                data-test="cy-generalIntroductionVirtualOther-text"
              >
                <RenderText
                  textKey="general_debtsView_welcome_virtual_description_others"
                  isMarkdown
                />
              </Typography>
              <Box className={classes.debtsBox} data-test="cy-debts-box">
                {outDebts.filter(({ type }) => type !== DebtType.Virtual).map((item) => (
                  <DebtCard key={item.id} debt={item as OutDebt} />
                ))}
              </Box>
            </>
          )
          : (
            <>
              <Typography className={cn(classes.debtsDescription, 'mb-3')} data-test="cy-generalIntroduction-text">
                <RenderText
                  textKey="general_debtsView_welcome_description"
                  isMarkdown
                />
              </Typography>
              <Box className={classes.debtsBox} data-test="cy-debts-box">
                {outDebts.map((item) => <DebtCard key={item.id} debt={item as OutDebt} />) }
              </Box>
            </>
          )
      }
      <WaCard
        withSeparator
        className={classes.waCard}
        waMessage={firstDebt
          ? `${gT('action_wa_button_message', { bank: firstDebt?.bank })}`
          : undefined}
        bank={firstDebt?.bank}
      />
    </Container>
  );
};

const useStyles = makeStyles((theme: TranquiTheme) => createStyles({
  root: {
    padding: theme.spacing(2, 3),
    boxSizing: 'border-box',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(4),
    },
  },
  debtsDescription: {
    ...theme.typography.body2,
    color: theme.palette.text3.main,
  },
  debtsBox: {
    display: 'grid',
    gap: `${theme.spacing(4)}px`,
    gridTemplateColumns: 'repeat( auto-fit, minmax(220px, 1fr) )',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat( auto-fill, minmax(220px, 1fr) )',
    },
  },
  virtualDebtsBox: {
    display: 'grid',
    gap: `${theme.spacing(4)}px`,
    gridTemplateColumns: 'repeat( auto-fit, minmax(320px, 1fr) )',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat( auto-fill, minmax(320px, 1fr) )',
    },
  },
  waCard: {
    transition: 'all .4s',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    '& p': {
      textAlign: 'center',
    },
  },
}));

export default SelectDebt;
